import classnames from 'classnames';
import merge from 'lodash.merge';  
import UAParser from 'ua-parser-js';
import moment from 'moment';

import { setGlobalVariable } from './globalVariableHelper';

import { NodeTypeScheme, FunctionUpdateChunk } from '../types/base';
import { collectTemplateVariables, expandTemplateRefs, withTemplateVariables } from './screenTemplates';
import { layerTemplateVariables } from './templateLocalVariables';
import { applyStyleClasses } from './styleClasses';

export type ScreenResolutions = '4k' | '2k' | 'laptopL' | 'laptop' | 'tablet' | 'mobileL' | 'mobileM' | 'mobileS';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseIfJSON(input: string): any | string {
  try {
    const parsedJSON = JSON.parse(input);
    return parsedJSON;
  } catch (error) {
    // Parsing failed, return the original input
    return input;
  }
}

export function stringifyIfJSON(input: any, pretty: boolean): string | any {
  if (typeof input === 'object' && input !== null) {
    try {
      // Add the second and third parameters to JSON.stringify for indentation
      const jsonString = JSON.stringify(input, null, pretty ? 2 : 0);
      return jsonString;
    } catch (error) {
      // Stringification failed, return the original input
      return '';
    }
  } else {
    // Input is not an object, return it as is
    return '';
  }
}

type ItemType = {
  id: string;
  active_state?: string;
  name?: string;
  component: string;
  value?: string | boolean;
  items?: ItemType[];
  style?: {
    states?: {
      loading?: any;
    };
  };
}

interface RadioUncheckResult {
  items: ItemType[];
  changedIds: string[];
}

export type InternalFunctions = {
  $setVariable: (key: string, value: any) => void;
  $getVariable: (key: string) => any;
  $moment: (...args: any[]) => moment.Moment;
  $response?: any;
  // TNBEWEB-1992: write path + repeatable-row context behind the data.* / _c.* / _config.* /
  // this.* handler objects. Supplied by buildInternalFns (handlerScope.ts); all optional so
  // any legacy caller passing the old shape keeps 100% of the previous behaviour.
  writeVar?: (path: string, value: any) => void;
  thisRow?: any;
  rowPath?: string;
  // TNBEWEB-2022: outcome hooks for an event's `pre` functions, supplied by buildInternalFns.
  // Never visible to author code — executeFunction picks its context keys explicitly.
  onPreCancelled?: (reason: unknown) => void;
  onPrePassed?: () => void;
}

// TNBEWEB-1992: recursive Proxy that turns property access into dotted-path get/set.
// `data.a.b = 1` writes path 'a.b'; `data.a.b` reads it. Nested objects/arrays return a
// child proxy so writes flow through at any depth; scalars return the raw (native-typed)
// value. Symbols (Symbol.iterator, Symbol.toPrimitive, …) delegate to the raw node so a
// nested array/object can still be iterated or spread.
const makeVarProxy = (
  basePath: string,
  readFn: (path: string) => any,
  writeFn: (path: string, value: any) => void
): any =>
  new Proxy(Object.create(null), {
    get(_t, prop) {
      if (typeof prop === 'symbol') {
        const node = readFn(basePath);
        if (node === null || node === undefined) {
          // A missing node coerces like `undefined` (NaN in arithmetic, "undefined" as a
          // string) rather than "[object Object]", so a leaf read of an unset var stays sane.
          if (prop === Symbol.toPrimitive) return () => undefined;
          return undefined;
        }
        const sv = (node as any)[prop];
        return typeof sv === 'function' ? sv.bind(node) : sv;
      }
      const full = basePath ? `${basePath}.${String(prop)}` : String(prop);
      const val = readFn(full);
      if (val === null) return null;
      // TNBEWEB-1992 (v2 data.* model): arrays read RAW so `.filter/.map/.find`, `Array.isArray`
      // and `|| []` behave natively — a child proxy breaks them and, once stored, corrupts the
      // array. Reassign the whole array rather than mutating an element in place.
      if (Array.isArray(val)) return val;
      // Plain objects AND not-yet-existing (undefined) paths return a child proxy, so a nested
      // write to a missing path — `data.form.address.city = 1` — creates it (the set trap gets
      // the full dotted path). Existing scalars return raw, matching $get. (Caveat: reading a
      // never-set key yields a truthy proxy, not undefined — screen vars are normally declared.)
      if (val === undefined || typeof val === 'object') return makeVarProxy(full, readFn, writeFn);
      return val;
    },
    set(_t, prop, value) {
      if (typeof prop === 'symbol') return false;
      writeFn(basePath ? `${basePath}.${String(prop)}` : String(prop), value);
      return true;
    },
    has(_t, prop) {
      if (typeof prop === 'symbol') return false;
      const full = basePath ? `${basePath}.${String(prop)}` : String(prop);
      return readFn(full) !== undefined;
    },
    ownKeys() {
      const node = readFn(basePath);
      return node !== null && typeof node === 'object' ? Reflect.ownKeys(node) : [];
    },
    getOwnPropertyDescriptor(_t, prop) {
      if (typeof prop === 'symbol') return undefined;
      const full = basePath ? `${basePath}.${String(prop)}` : String(prop);
      const val = readFn(full);
      if (val === undefined) return undefined;
      return { value: val, enumerable: true, configurable: true, writable: true };
    },
  });

// TNBEWEB-1992: builds the `data` / `_c` / `_config` / `this` scope objects from the handler's
// internalFns closures. Reads go through fns.$getVariable (identical to $get, including its
// local-then-global routing); writes go through fns.writeVar. `_c` and `_config` are the SAME
// reference (a write via one is visible via the other). `this` is undefined outside a row.
export const buildHandlerScope = (fns: InternalFunctions) => {
  const read = (p: string) => fns.$getVariable(p);
  const write = (p: string, v: any) => fns.writeVar?.(p, v);
  const data = makeVarProxy('', read, write);
  // Global variables are addressed by the string-form 'c.' prefix; _c.x ≡ $get('c.x').
  const globalScope = makeVarProxy(
    '',
    (p) => read(`c.${p}`),
    (p, v) => write(`c.${p}`, v)
  );
  const hasRow = fns.thisRow !== null && fns.thisRow !== undefined && typeof fns.rowPath === 'string';
  const thisArg = hasRow
    ? makeVarProxy(
      '',
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      (p) => getNestedValue(fns.thisRow, p),
      (p, v) => write(`${fns.rowPath}.${p}`, v)
    )
    : undefined;
  // `global` is the single proxy behind both _c and _config (see executeFunction).
  return { data, global: globalScope, thisArg };
};

export const executeFunction = (
  functionString: string,
  $event?: React.MouseEvent<HTMLDivElement | HTMLTextAreaElement | HTMLInputElement> | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  internalFns?: InternalFunctions,
  // When true, errors propagate to the caller instead of being swallowed. Used by
  // `pre`-timing functions: a thrown / rejected `pre` function must abort the event's
  // requests. Default false keeps legacy `during` callers fire-and-forget.
  rethrowErrors = false
) => {
  try {
    // Check for state function calls. The short names also match the long names
    // (e.g. "$setVariable".includes("$set")), so a single substring check covers both.
    const hasSetState = functionString.includes('$set');
    const hasGetState = functionString.includes('$get');

    // TNBEWEB-1992: data / _c / _config / this. Built only when the caller supplied the new
    // write path (writeVar); otherwise `scope` is undefined and behaviour is 100% legacy.
    const scope = internalFns?.writeVar ? buildHandlerScope(internalFns) : undefined;
    // Inject a scope object only when the handler references it as a WHOLE WORD and does not
    // declare its own binding of that name (a strict-mode `let/const/function/class <id>`
    // clashing with a same-named parameter is a SyntaxError). `data`/`_c`/`_config` were never
    // valid bare identifiers before (no closure provided them), so injecting can only fix a
    // ReferenceError, never change a working handler.
    const wants = (id: string) =>
      new RegExp(`\\b${id}\\b`).test(functionString) &&
      !new RegExp(`\\b(?:let|const|function|class)\\s+${id}\\b`).test(functionString);

    // Create context with allowed internal functions.
    // $set / $get are short aliases of $setVariable / $getVariable (TNBEWEB-1987);
    // the long names remain as deprecated aliases for backward compatibility.
    const context: Record<string, any> = {
      $event,
      $response: internalFns?.$response,
      ...(hasSetState && { $setVariable: internalFns?.$setVariable, $set: internalFns?.$setVariable }),
      ...(hasGetState && { $getVariable: internalFns?.$getVariable, $get: internalFns?.$getVariable }),
      ...(scope && wants('data') && { data: scope.data }),
      ...(scope && wants('_c') && { _c: scope.global }),
      ...(scope && wants('_config') && { _config: scope.global }),
    };

    // Create function with controlled scope
    const func = new Function(...Object.keys(context), `
      "use strict";
      ${functionString}
    `);

    // Execute with provided context. `this` is the current repeatable row (a Proxy) when one
    // exists; otherwise undefined — identical to the previous plain call under strict mode.
    // Return the result so callers can await a returned Promise (`pre` timing).
    return func.apply(scope?.thisArg, Object.values(context));
  } catch (error) {
    // A `pre` caller reports the failure itself (reportPreCancelled); logging here as well
    // printed every cancelled event twice.
    if (rethrowErrors) throw error;
    console.error('Error executing function:', error);
  }
};

type HandlerEvent = React.MouseEvent<HTMLDivElement | HTMLTextAreaElement | HTMLInputElement>
  | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

// A function result is async only when it is a thenable. Reading `then` can itself throw
// (a hostile getter) — callers invoke this inside their try, so that counts as a failure.
const isThenable = (value: unknown): value is PromiseLike<unknown> => value !== null
  && (typeof value === 'object' || typeof value === 'function')
  && typeof (value as PromiseLike<unknown>).then === 'function';

// TNBEWEB-2022: one split for both event paths (components and the on_success / on_error saga).
export const splitFunctionsByTiming = (functionUpdates: FunctionUpdateChunk[] = []) => ({
  pre: functionUpdates.filter((u) => u?.function && u.timing === 'pre'),
  during: functionUpdates.filter((u) => u?.function && u.timing !== 'pre'),
});

// TNBEWEB-2022: runs `pre` functions in order and stays SYNCHRONOUS for as long as each returns
// a non-thenable, so the effects that follow still run in the originating event's call stack
// (a window.open there keeps the user gesture — identical to an event without pre functions).
// Only a function that returns a thenable switches to async: the returned Promise settles after
// it and every pre function after it. A throw propagates synchronously; a rejection rejects.
export const runPreFunctions = (
  preFns: FunctionUpdateChunk[],
  internalFns: InternalFunctions,
  $event?: HandlerEvent
): void | Promise<void> => {
  const runFrom = (start: number): void | Promise<void> => {
    for (let i = start; i < preFns.length; i += 1) {
      const result = executeFunction(preFns[i].function, $event, internalFns, true);
      if (isThenable(result)) {
        const next = i + 1;
        // Adopt the thenable ONCE: a hand-rolled `then` that calls back twice must not re-run
        // the pre functions after it.
        return Promise.resolve(result).then(() => runFrom(next));
      }
    }
    return undefined;
  };
  return runFrom(0);
};

// A `pre` function threw / rejected: the event's styles / actions / requests are skipped and
// no on_error is raised at this level. The owner hook makes it visible on the component.
export const reportPreCancelled = (internalFns: InternalFunctions | undefined, reason: unknown) => {
  console.warn('Event cancelled by its "pre" function; its styles, actions and requests were skipped:', reason);
  internalFns?.onPreCancelled?.(reason);
};

// Runs an event's JS functions honouring their `timing`, then the event's other
// effects (styles / actions / requests, supplied by the caller as `runEffects`):
//   - `during` (default): start immediately, in parallel with the effects (legacy behaviour).
//   - `pre`: run all pre functions FIRST, then the effects. If any pre function
//     throws / returns a rejected Promise, the effects are SKIPPED (validation slot —
//     requests do not fire and no `on_error` is triggered at this level).
// Synchronous pre functions keep the effects in the event's own call stack; only a pre
// function that returns a Promise defers them. Shared by every screen component
// (Container, Input, Select, Text, Checkbox, Radio, Switcher, DateRangePicker, Chart).
export const runFunctionsWithTiming = (
  functionUpdates: FunctionUpdateChunk[],
  internalFns: InternalFunctions,
  runEffects: () => void,
  $event?: HandlerEvent
) => {
  const { pre, during } = splitFunctionsByTiming(functionUpdates);

  // during: fire-and-forget, before/parallel with the effects
  during.forEach((update) => {
    executeFunction(update.function, $event, internalFns);
  });

  if (!pre.length) {
    runEffects();
    return;
  }

  let pending: void | Promise<void>;
  try {
    pending = runPreFunctions(pre, internalFns, $event);
  } catch (err) {
    reportPreCancelled(internalFns, err);
    return;
  }

  if (!pending) {
    internalFns.onPrePassed?.();
    runEffects();
    return;
  }

  pending
    .then(() => {
      internalFns.onPrePassed?.();
      runEffects();
    }, (err) => reportPreCancelled(internalFns, err))
    // A throw inside the effects is not a cancelled pre function — report it as what it is.
    .catch((err) => console.error('Event effects failed after its "pre" functions:', err));
};

// Helper function to get nested object values using string path
export const getNestedValue = (obj: any, path: string) => {
  // Handle empty/invalid paths
  if (!path) return undefined;

  // Split path into parts (e.g. "user.address.street" -> ["user", "address", "street"])
  const parts = path.split('.');
  let result = obj;

  // Traverse the object following the path
  for (const part of parts) {
    if (result === null || result === undefined) {
      return undefined;
    }
    result = result[part];
  }

  return result;
};

// Helper function to set nested object values using string path
export const setNestedValue = (obj: any, path: string, value: any) => {
  // Handle empty/invalid paths
  if (!path) return;

  // Split path into parts (e.g. "user.address.street" -> ["user", "address", "street"])
  const parts = path.split('.');
  let current = obj;

  // Navigate to the parent of the target property
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (current[part] === null || typeof current[part] !== 'object') {
      current[part] = {};
    }
    current = current[part];
  }

  // Set the final value
  current[parts[parts.length - 1]] = value;
};

export const isClickInsidePortal = (node: Node | null): boolean => {
  while (node) {
    if (node instanceof HTMLElement && node.getAttribute('data-portal') === 'true') {
      return true;
    }
    node = node.parentNode;
  }
  return false;
};

export const debounce = (func: Function, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;
  return function deb (...args: any[]) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
};

// Helper function to check if nodeId matches the pattern
export const matchesPattern = (pattern: string, nodeId: string): boolean => {
  const regex = new RegExp(`^${pattern.replace('*', '.*')}$`);
  return regex.test(nodeId);
};

export function formShimmerCacheKey(obj: { command?: string, action?: string, component_id?: string, screen_id?: string }): string {
  const { command, action, component_id, screen_id } = obj || {};
  let cacheKey = command;

  if (component_id && screen_id) {
    if (action) {
      cacheKey = `${screen_id}/${component_id}/${action}`;
    } else if (command) {
      cacheKey = `${screen_id}/${component_id}/${command}`;
    }
  }

  return cacheKey;
}

export function transformShimmerCache(scheme: object[]): {
  status: boolean;
  transformed: object[];
} {
  let hasCache = false;
  const pattern = /{{.*}}/;

  function analyzeObject(obj: any): any {
    // Create copy of object to avoid mutations
    const result = { ...obj };

    // Remove any existing events
    // if (result.events) {
    //   delete result.events;
    // }

    // Check if current object has cache:true or value matches the pattern
    if (
      obj.cache === true ||
      (typeof obj.value === 'string' && pattern.test(obj.value) && obj.cache !== false)
    ) {
      hasCache = true;
      result.skeleton = true;
      return result;
    }

    // Only process if items array exists
    if (Array.isArray(obj.items)) {
      result.items = obj.items.map((item) => analyzeObject(item));
    }

    return result;
  }

  const transformed = scheme.map((obj) => analyzeObject(obj));

  return {
    status: hasCache,
    transformed,
  };
}

export function clearSkeletonProperttyFromScheme(scheme: any): any {
  function clearObject(obj: any): any {
    // Create copy of object to avoid mutations
    const result = { ...obj };

    // Remove skeleton property if it exists
    if (result.skeleton !== undefined) {
      delete result.skeleton;
    }

    // Recursively process items array if it exists
    if (Array.isArray(obj.items)) {
      result.items = obj.items.map((item) => clearObject(item));
    }

    return result;
  }

  return scheme.map((obj) => clearObject(obj));
}

export function recursiveUpdateById(
  items: ItemType[],
  idsObject: Record<string, any>,
  updateFunc: (item: ItemType, insideTable: boolean) => ItemType,
  insideTable: boolean = false
): ItemType[] {
  return items.map((item) => {
    const isTable = item.component === "table";
    const newInsideTable = insideTable || isTable;

    // Check if item.id matches any pattern in ids or if item.id is directly in ids
    const idMatches = Object.keys(idsObject).some((id) => id === item.id || (matchesPattern(id, item.id) && idsObject[id]?.object_type === item.component));
    if (idMatches) {
      item = updateFunc({ ...item }, newInsideTable);
    }
    if (item.items && item.items.length > 0) {
      return { ...item, items: recursiveUpdateById(item.items, idsObject, updateFunc, newInsideTable) };
    }
    return item;
  });
}

// A value that is ENTIRELY an unresolved `{{...}}` template — e.g. the literal
// `"{{parameters.repayment_schedule_details}}"` the backend leaves behind when a
// dynamic value reference fails to resolve (see `variable_data._errors` /
// `parameters.*`). It carries no data.
const isUnresolvedVariablePlaceholder = (v: unknown): boolean =>
  typeof v === 'string' && /^\{\{.*\}\}$/.test(v);

// Deep-merge variable bags left-to-right (later sources override earlier ones),
// but NEVER let an unresolved `{{placeholder}}` string from a later source
// clobber an already-resolved value from an earlier one.
//
// Why this exists: the screen's `settings.variables` holds the fully-resolved
// objects (e.g. `scoring_details` with its `docs.list` array), while the
// backend's `parameters` bag leaves un-resolvable references as literal
// `"{{parameters.X}}"` strings. A plain `lodash.merge` (string overwrites
// object) replaces the resolved object with that string, so anything bound to
// `{{X.docs.list}}` renders empty — until a later UPDATE_VARIABLE_SUCCESS merges
// a real OBJECT into the key, at which point the deep-merge finally preserves
// `settings.variables`' resolved data and the content appears (the
// "only shows after I expand/collapse the block" symptom). Guarding the merge
// keeps the resolved value so it renders on first paint.
export function mergeVariableBags(...sources: any[]): any {
  const result: any = {};
  sources.forEach((src) => {
    if (!src || typeof src !== 'object') return;
    const safe: any = {};
    Object.keys(src).forEach((key) => {
      const incoming = src[key];
      const existing = result[key];
      if (isUnresolvedVariablePlaceholder(incoming)
        && existing !== undefined
        && !isUnresolvedVariablePlaceholder(existing)) {
        return; // keep the resolved value; drop the clobbering placeholder
      }
      safe[key] = incoming;
    });
    merge(result, safe); // lodash deep-merge handles nested objects + cloning
  });
  return result;
}

export function mergeLocalVariablesIntoGlobal(globalVariables?: any, localVariables?: any) {
  return mergeVariableBags(localVariables || {}, globalVariables || {});
}

export function reccursivelyUncheckRadioItemsLegacy(
  items: ItemType[], 
  name: string, 
  excludeId: string
): RadioUncheckResult {
  const changedIds: string[] = [];

  const updatedItems = items.map((item) => {
    if (item.component === 'radio' && item.name === name && item.id !== excludeId) {
      item.value = false;
      changedIds.push(item.id);
    } else if (item.component === 'radio' && item.name === name && item.id === excludeId) {
      item.value = true;
    }

    if (item.items && item.items.length > 0) {
      const nestedResult = reccursivelyUncheckRadioItemsLegacy(item.items, name, excludeId);
      changedIds.push(...nestedResult.changedIds);
      return { ...item, items: nestedResult.items };
    }

    return item;
  });

  return {
    items: updatedItems,
    changedIds
  };
}

export function attachStyle(style: string) {
  const tag = document.createElement('style');
  tag.innerHTML = style;
  document.head.appendChild(tag);
}

export function isUndefined(value?: string | null | { [key: string]: string }) {
  return typeof value === 'undefined' || value === null || value.length === 0;
}

export function random() {
  return Math.floor(Math.random() * 0xffff);
}

export function replaceKeyWithCssVar(inputString: string) {
  if (!inputString || !inputString.length) return inputString;

  return inputString.replace(/\$[a-zA-Z0-9_-]+/g, match => `var(--${match.slice(1)})`);
}

export const combineClassNames = (...classes) => {
  const processedClasses = classes.reduce((acc, className) => {
    if (typeof className === 'string') {
      acc.push(...className.split(' '));
    } else if (Array.isArray(className)) {
      acc.push(...className);
    } else if (className && typeof className === 'object') {
      Object.keys(className).forEach((key) => {
        if (className[key]) {
          acc.push(key);
        }
      });
    }
    return acc;
  }, []);

  return classnames(...processedClasses);
};

export const resolveSpacingMargin = (spacing?: Array<number | string> | string | number | null): Record<string, string> => {
  if (!spacing) {
    return {};
  }
  const parseValue = (val: number | string): string => {
    if (typeof val === 'number') {
      return `${val}px`;
    } if (typeof val === 'string' && val.trim() !== '') {
      return val.includes('px') || val.includes('%') ? val : `${parseFloat(val)}px`;
    }
    return '';
  };

  if (Array.isArray(spacing)) {
    const [top, right, bottom, left] = spacing.map(parseValue);

    const result: Record<string, string> = {};
    if (top) result.marginTop = top;
    if (right) result.marginRight = right;
    if (bottom) result.marginBottom = bottom;
    if (left) result.marginLeft = left;

    return result;
  } if (typeof spacing === 'number' || (typeof spacing === 'string' && spacing.trim() !== '')) {
    const value = parseValue(spacing);
    return {
      marginTop: value,
      marginRight: value,
      marginBottom: value,
      marginLeft: value,
    };
  }

  return {};
};

export const resolveSpacingPadding = (spacing?: Array<number | string> | string | number | null): Record<string, string> => {
  if (!spacing) {
    return {};
  }
  const parseValue = (val: number | string): string => {
    if (typeof val === 'number') {
      return `${val}px`;
    } 
    if (typeof val === 'string' && val.trim() !== '') {
      return val.includes('px') || val.includes('%') ? val : `${parseFloat(val)}px`;
    }
    return '';
  };

  if (Array.isArray(spacing)) {
    const [top, right, bottom, left] = spacing.map(parseValue);

    const result: Record<string, string> = {};
    if (top) result.paddingTop = top;
    if (right) result.paddingRight = right;
    if (bottom) result.paddingBottom = bottom;
    if (left) result.paddingLeft = left;

    return result;
  } if (typeof spacing === 'number' || (typeof spacing === 'string' && spacing.trim() !== '')) {
    const value = parseValue(spacing);
    return {
      paddingTop: value,
      paddingRight: value,
      paddingBottom: value,
      paddingLeft: value,
    };
  }

  return {};
};

export function isValidSvg(svg_string: string) {
  if (typeof svg_string !== 'string') {
    return false;
  }

  if (!svg_string.trim().match(/^<svg[^>]*>[\s\S]*<\/svg>$/i)) {
    return false;
  }
  const parser = new DOMParser();
  const doc = parser.parseFromString(svg_string, 'image/svg+xml');

  return doc.documentElement.nodeName === 'svg';
}

export function getDeviceInfo() {
  const parser = new UAParser();
  const browserInfo = parser.getResult();

  const geodata = null;
  if (navigator.permissions) {
    // const permission = await navigator.permissions.query({ name: 'geolocation' });
    // if (permission.state === 'granted') {
    //   geodata = await new Promise(resolve => navigator.geolocation.getCurrentPosition(position => resolve(position.coords)));
    // }
  }

  return {
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    offset: -new Date().getTimezoneOffset(),
    localization: navigator.language || null,
    device_id: null,
    device_os: browserInfo.os.name || null,
    browser_version: browserInfo.browser.version || null,
    browser_provider: browserInfo.browser.name || null,
    front_version: "0.1",
    back_version: "0.1",
    screen_resolution: {
      w: window.screen.width || null,
      h: window.screen.height || null
    },
    device_type: browserInfo.device.type || 'desktop',
    external_ip: null,
    internal_ip: null,
    geodata: geodata ? {
      latitude: geodata.latitude,
      longitude: geodata.longitude,
      altitude: geodata.altitude,
      accuracy: geodata.accuracy,
      altitude_accuracy: geodata.altitudeAccuracy,
      heading: geodata.heading,
      speed: geodata.speed
    } : null
  };
}

// Helper function to parse form values
export function parseFormValues(section: any) {
  const formValues: Record<string, any> = {};
  const parseItems = (items: any[]) => {
    items.forEach(item => {
      if (item.id && item.value !== undefined && 
        (item.component === 'input' || item.component === 'checkbox' || item.component === 'radio' || item.component === 'select' || item.component === 'switcher')
      ) {
        formValues[item.id] = item.value;
      }
      if (item.items && item.items.length > 0) {
        parseItems(item.items);
      }
    });
  };

  if (Array.isArray(section)) {
    section.forEach((sec) => {
      if (sec.items) {
        parseItems(sec.items);
      }
    });
  }

  if (section && section.items) {
    parseItems(section.items);
  }

  return formValues;
}

export const isInCurlyBracesFormat = (value: string) => {
  const regex = /^\{\{.*\}\}$/;
  return regex.test(value);
};

export const extractAllValuesInsideCurlyBraces = (value: any): string[] => {
  if (typeof value !== 'string') {
    return [stringifyIfJSON(value, false)];
  }
  const matches = [...value.matchAll(/\{\{(.*?)\}\}/g)];
  return matches.map(match => match[1]);
};

export const extractValueInsideCurlyBraces = (value: string | undefined): string => {
  if (!value) return '';
  const match = value.match(/\{\{(.*?)\}\}/);
  return match ? match[1] : value;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getStringValueFromPath = (obj: Record<string, any>, path: string | string[]): string => {
  // Handle the case where the path is exactly 'this'
  if (path === 'this') {
    return typeof obj === 'string' ? obj : JSON.stringify(obj);
  }

  if (Array.isArray(path)) {
    const normalizedPaths = path.map(p => p.startsWith('this.') ? p.slice(5) : p);
    const values = normalizedPaths.map(p => p.split('.').reduce((acc, part) => acc && acc[part], obj));
    return values.map(value => typeof value === 'string' ? value : JSON.stringify(value)).join('');
  } 
      // Remove 'this.' prefix if it exists
  const normalizedPath = path.startsWith('this.') ? path.slice(5) : path;
  const value = normalizedPath.split('.').reduce((acc, part) => acc && acc[part], obj);
  return typeof value === 'string' ? value : JSON.stringify(value);
  

};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getNestedValue2 = (obj: any, path: string): any => path.split('.').reduce((acc, part) => {
  const value = (acc !== undefined && acc !== null && typeof acc === 'object' && !Array.isArray(acc)) ? acc[part] : acc;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return value.toString();
  }
  return value;
}, obj);

// Keys whose pre-substitution template is preserved as `<key>Original` when page
// init resolves the scheme. `value` is long-standing; `to` is FE-4677.
// Blast radius for `to` is nil: it is never a constructed object key in this repo,
// it only ever arrives from scheme JSON as `ActionChunk.to`.
const TEMPLATE_ORIGIN_KEYS = new Set(['value', 'to']);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const replacePlaceholdersWithVariables = (style: any, variables: any): any => {
  const parsedVars = typeof variables === 'string' ? parseIfJSON(variables) : variables;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const replacePlaceholders = (obj: any): any => {
    if (typeof obj === 'string') {
      const regex = /{{(.*?)}}/g;
      let match;
      let result = obj;
      const matches = [];
      // eslint-disable-next-line no-cond-assign
      while ((match = regex.exec(obj)) !== null) {
        matches.push(match);
      }
      // eslint-disable-next-line @typescript-eslint/no-shadow
      matches.forEach((match) => {
        if (match[0].startsWith('{{this') || match[0].startsWith('{{_l.')) {
          return;
        }
  
        const variablePath = match[1].trim();
        const variableValue = getNestedValue2(parsedVars, variablePath);
        // eslint-disable-next-line no-nested-ternary
        const replacement = variableValue !== undefined ? (typeof variableValue === 'object' ? JSON.stringify(variableValue) : variableValue) : '';
        result = result.replace(match[0], replacement);
      });
      return result;
    } if (Array.isArray(obj)) {
      return obj.map((item) => replacePlaceholders(item));
    } if (typeof obj === 'object' && obj !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newObj: { [key: string]: any } = {};
      Object.keys(obj).forEach((key) => {
        const processed = replacePlaceholders(obj[key]);
        newObj[key] = processed;
        
        // If this key carries a template that must survive page init, stash the
        // pre-substitution string in a sibling `<key>Original`.
        // FE-4677: `to` joined `value` here. Page init runs this resolver over the
        // WHOLE scheme (routes/page/index.tsx), including `events.*.action.to`, so an
        // endpoint path like `/admin/loan/{{loan_ref}}` was baked to `/admin/loan/`
        // before any click could happen — the template was gone by event time.
        if (TEMPLATE_ORIGIN_KEYS.has(key) && typeof obj[key] === 'string' && typeof processed === 'string' && obj[key] !== processed) {
          newObj[`${key}Original`] = obj[key];
        }
      });
      return newObj;
    }
    return obj;
  };

  return replacePlaceholders(style);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const replacePlaceholdersWithVariablesV2 = (style: any, variables: any): any => {
  const parsedVars = typeof variables === 'string' ? parseIfJSON(variables) : variables;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const replacePlaceholders = (obj: any): any => {
    if (typeof obj === 'string') {
     const regex = /{{(this(?:\..*?)?)}}/g;
      let match;
      let result = obj;
      const matches = [];
      // eslint-disable-next-line no-cond-assign
      while ((match = regex.exec(obj)) !== null) {
        matches.push(match);
      }
      matches.forEach((match) => {
        const variablePath = match[1].trim();
        let replacement;
        if (variablePath === 'this') {
          replacement = JSON.stringify(parsedVars);
        } else {
          const variableValue = getNestedValue2(parsedVars, variablePath.slice(5)); // Remove 'this.' from the path
          replacement = variableValue !== undefined ? (typeof variableValue === 'object' ? JSON.stringify(variableValue) : variableValue) : '';
        }
        result = result.replace(match[0], replacement);
      });
      return result;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => replacePlaceholders(item));
    }
    if (typeof obj === 'object' && obj !== null) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newObj: { [key: string]: any } = {};
      Object.keys(obj).forEach((key) => {
        if (key === 'object_id') {
          newObj[key] = obj[key]; // Ignore/skip placeholder replacement for 'object_id'
        } else {
          newObj[key] = replacePlaceholders(obj[key]);
        }
      });
      return newObj;
    }
    return obj;
  };

  return replacePlaceholders(style);
};

export function attachScriptsToHeader(scriptUrls: string) {
  const scriptTagRegex = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
  const srcAttrRegex = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)')/i;

  let match;
  while ((match = scriptTagRegex.exec(scriptUrls)) !== null) {
    const [fullTag, attrs, inner] = match;
    const srcMatch = attrs.match(srcAttrRegex);
    const src = srcMatch && (srcMatch[1] ?? srcMatch[2]);

    if (src) {
      attachScriptIfMissing(src, false);
    } else if (inner.trim()) {
      attachScriptIfMissing(fullTag, true);
    }
  }
}

export async function attachFontsToHeader(fonts: string | { name: string, src: string }[]): Promise<void> {
  const appendLink = (href: string) => {
    if (!href) return;
    if (document.querySelector(`link[rel="stylesheet"][href="${href}"]`)) return;
    const linkElement = document.createElement('link');
    linkElement.rel = 'stylesheet';
    linkElement.href = href;
    document.head.appendChild(linkElement);
  };

  // Editor serializes `fonts` as `JSON.stringify([{name, src}, ...])` — the
  // string branch must JSON.parse first. Legacy `<link href="...">` HTML
  // payloads still flow through the regex fallback below.
  let parsed: string | { name: string, src: string }[] | null = null;
  if (typeof fonts === 'string' && fonts) {
    try {
      const maybe = JSON.parse(fonts);
      if (Array.isArray(maybe)) parsed = maybe;
    } catch {
      parsed = null;
    }
  }
  const source = parsed ?? fonts;

  if (typeof source === 'string') {
    const fontRegex = /<link.*?href="([^"]+)"[^>]*>/gs;
    let match: RegExpExecArray | null;
    while ((match = fontRegex.exec(source)) !== null) {
      appendLink(match[1]);
    }
  } else if (Array.isArray(source)) {
    source.forEach((font) => {
      if (typeof font === 'string') {
        appendLink(font);
      } else if (font && font.name && font.src) {
        appendLink(font.src);
      }
    });
  }
}

function attachScriptIfMissing(scriptContent: string, isInline: boolean) {
  const existingScripts = document.querySelectorAll('script');

  if (!isInline) {
    // Check if the URL script is already present
    for (let i = 0; i < existingScripts.length; i++) {
      if (existingScripts[i].src === scriptContent) {
        return; // Script already exists
      }
    }

    // Attach the URL script
    const script = document.createElement('script');
    script.src = scriptContent;
    document.head?.appendChild(script);
  } else {
    // Extract the inner content of the inline script
    const inlineScriptContent = extractScriptContent(scriptContent);

    // Check if the inline script is already present
    for (let i = 0; i < existingScripts.length; i++) {
      if (existingScripts[i].innerHTML.trim() === inlineScriptContent) {
        return; // Script already exists
      }
    }

    // Attach the inline script
    const script = document.createElement('script');
    script.innerHTML = inlineScriptContent;
    document.head?.appendChild(script);
  }
}

function extractScriptContent(content: string): string {
  const scriptRegex = /<script.*?>([\s\S]*?)<\/script>/s;
  const match = scriptRegex.exec(content);

  return match ? match[1].trim() : '';
}

// urlHelper.ts
export const handleOpenUrl = (value: string, delay: number, isOpenNewTab: boolean, hist?: any) => {
  const openUrl = () => {
    const url = value.startsWith('/') ? `${window.location.origin}${value}` : value;
    const isDownloadable = url.match(/\.(pdf|docx?|xlsx?|zip|rar|tar\.gz|jpg|jpeg|png|gif)$/i);

    if (isDownloadable) {
      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener noreferrer';
      link.target = '_blank';
      link.download = '';
      link.click();
    } else if (isOpenNewTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
    } else if (value.startsWith('/')) {
      if (hist) { 
        hist.push(value);
     } else if (!hist) { window.location.href= value; }
    } else {
      window.location.href = url;
    }
  };

  if (delay && delay > 0) {
    setTimeout(openUrl, delay * 1000);
  } else {
    openUrl();
  }
};

export const isStringVariable = (source?: string): boolean => /^\{\{(.*)\}\}$/.test(source || '');

// eslint-disable-next-line @typescript-eslint/no-explicit-any, max-len
export function getCorrespondingScheme(data: NodeTypeScheme, parentData: NodeTypeScheme, dataLake: any, i: number, variables: any, parentI = 0): any {
  if (data?.type === 'table-header-row' || data?.type === 'table-body-row') {
    // eslint-disable-next-line max-len
    const replacedVariables = isStringVariable(parentData?.settings?.source) ? replacePlaceholdersWithVariables(parentData?.settings?.source, variables) : parentData?.settings?.source;
    const parseSourceIfExist = parseIfJSON(replacedVariables);
    // TNBEWEB-1992: capture the bound variable name from the raw `{{var}}` template (before it
    // is resolved to the array) so a `this.*` write inside a row targets variableData.<var>.<i>.
    // Stashed on settings too, because the row's cells only see the already-resolved source.
    // FE-4733: `-` admitted — a template local qualifies to `<placement-id>.<name>`, and a
    // placement id is hyphenated; without it the row loses its write-back path.
    const srcMatch = String(parentData?.settings?.source ?? '').match(/^\s*\{\{\s*([\w.-]+)\s*\}\}\s*$/);
    const srcVar = srcMatch ? srcMatch[1] : undefined;
    return {
      settings: { ...parentData?.settings, source: replacedVariables, ...(srcVar && { __sourceVar: srcVar }) },
      cols: parentData?.cols,
      data: Array.isArray(parseSourceIfExist) ? parseSourceIfExist[i] : undefined,
      rowPath: srcVar ? `${srcVar}.${i}` : undefined,
    };
  }
  if (data?.type === 'table-header-row-cell') {
    // eslint-disable-next-line max-len
    const replacedVariables = isStringVariable(dataLake?.settings?.source) ? replacePlaceholdersWithVariables(dataLake?.settings?.source, variables) : dataLake?.settings?.source;
    return {
      settings: { ...dataLake?.settings, source: replacedVariables },
      cols: dataLake?.cols,
      data: dataLake?.cols?.items?.[i],
    };
  }
  if (data?.type === 'table-body-row-cell') {
    const parseSourceIfExist = parseIfJSON(dataLake?.settings?.source);
    // TNBEWEB-1992: the row index for a cell is parentI; recover the bound variable name the
    // row stashed on settings so `this.*` writes inside the cell reach variableData.<var>.<parentI>.
    // eslint-disable-next-line no-underscore-dangle
    const srcVar = (dataLake?.settings as any)?.__sourceVar;
    return {
      settings: dataLake?.settings,
      cols: dataLake?.cols,
      data: Array.isArray(parseSourceIfExist) ? parseSourceIfExist[parentI] : undefined,
      rowPath: srcVar ? `${srcVar}.${parentI}` : undefined,
    };
  }
  if (dataLake) {
    return dataLake;
  }
  return undefined;
}

// Custom resolutions are keyed by an arbitrary id (not a preset name); their
// pixel widths live in screen.settings.custom_resolutions. The editor keys
// component `breakpoints` by that same id, so the runtime must be able to map
// a custom id -> width to emit the correct @media rule. We register them at
// parse time so getSizesBasedOnScreen (called deep in styleHelper without
// access to settings) can resolve them.
const customResolutionWidths: Record<string, number> = {};

export const registerCustomResolutions = (
  list?: { id?: string; width?: number }[] | null,
): void => {
  if (!Array.isArray(list)) return;
  list.forEach((res) => {
    if (res && typeof res.id === 'string' && typeof res.width === 'number') {
      customResolutionWidths[res.id] = res.width;
    }
  });
};

export const getSizesBasedOnScreen = (screen: ScreenResolutions): number => {
  switch (screen) {
    case '4k':
      return 2560;
    case '2k':
      return 1920;
    case 'laptopL':
      return 1440;
    case 'laptop':
      return 1024;
    case 'tablet':
      return 768;
    case 'mobileL':
      return 425;
    case 'mobileM':
      return 375;
    case 'mobileS':
      return 320;
    default:
      // Custom resolution id -> its persisted pixel width; 1440 if unknown.
      return (screen && customResolutionWidths[screen]) || 1440;
  }
};

/**
 * Migrate legacy camelCase protocol fields (active_state, svg_string, script_url,
 * settings.after_symbols) to snake_case on a single object. Per TNBEWEB-1772 the
 * scheme protocol is snake_case; older payloads may still arrive with the old
 * keys, so we normalize at parse time and let downstream code read only the
 * snake_case form.
 */
export function migrateLegacyProtocolFields(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;
  const out = { ...obj };
  if ('activeState' in out) {
    out.active_state = out.activeState;
    delete out.activeState;
  }
  if ('svgString' in out) {
    out.svg_string = out.svgString;
    delete out.svgString;
  }
  if ('scriptUrl' in out) {
    out.script_url = out.scriptUrl;
    delete out.scriptUrl;
  }
  if (out.settings && typeof out.settings === 'object' && 'afterSymbols' in out.settings) {
    out.settings = { ...out.settings, after_symbols: out.settings.afterSymbols };
    delete out.settings.afterSymbols;
  }
  return out;
}

function eventChunksHaveLegacy(events: any): boolean {
  if (!events || typeof events !== 'object') return false;
  for (const key of Object.keys(events)) {
    const chunks = events[key];
    if (!Array.isArray(chunks)) continue;
    for (const chunk of chunks) {
      if (chunk && typeof chunk === 'object' && nodeHasLegacy(chunk)) return true;
    }
  }
  return false;
}

function nodeHasLegacy(node: any): boolean {
  if (!node || typeof node !== 'object') return false;
  if ('activeState' in node || 'svgString' in node || 'scriptUrl' in node) return true;
  if (node.settings && typeof node.settings === 'object' && 'afterSymbols' in node.settings) return true;
  if (node.events && eventChunksHaveLegacy(node.events)) return true;
  if (Array.isArray(node.items)) {
    for (const item of node.items) {
      if (nodeHasLegacy(item)) return true;
    }
  }
  return false;
}

function migrateEventChunks(events: any): any {
  if (!events || typeof events !== 'object') return events;
  const out: any = {};
  Object.keys(events).forEach((key) => {
    const chunks = events[key];
    out[key] = Array.isArray(chunks)
      ? chunks.map((chunk: any) => migrateLegacyProtocolFields(chunk))
      : chunks;
  });
  return out;
}

/**
 * Walk a scheme node tree and migrate legacy protocol fields on every node
 * (including events and nested items). Returns a new tree; original is not mutated.
 * Once the BE is fully migrated, schemes will contain no legacy fields and the
 * scan-only fast path returns the original reference without rebuilding.
 */
export function migrateSchemeNode(node: any): any {
  if (!node || typeof node !== 'object') return node;
  if (Array.isArray(node)) return node.map(migrateSchemeNode);
  if (!nodeHasLegacy(node)) return node;
  const migrated = migrateLegacyProtocolFields(node);
  if (migrated.events) {
    migrated.events = migrateEventChunks(migrated.events);
  }
  if (Array.isArray(migrated.items)) {
    migrated.items = migrated.items.map(migrateSchemeNode);
  }
  return migrated;
}

// Refine <html lang> from the screen protocol's `localization` field.
// On bootstrap the document carries the project-default language; the screen
// protocol response is the first point the actual screen language is known, so
// applying it here refines the bootstrap default. No-op when the response omits
// localization, so we never clobber a known language with `undefined`.
export const applyDocumentLanguage = (localization?: unknown): void => {
  if (typeof localization !== 'string') return;
  const lang = localization.trim();
  if (!lang) return;
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = lang;
  }
};

/**
 * Bring a raw `settings` bag up to the shape the components read, for entry points that
 * don't go through parseOldFormat/parseNewFormat — today that's the editor's view-mode
 * iframe handshake, which posts the editor's own working config instead of an API payload.
 *
 * Two normalizations, both load-bearing:
 *   - `variables` may arrive as a JSON STRING (the editor stores the bag as editable text).
 *     `mergeVariableBags` skips non-object sources, so a string drops the ENTIRE screen
 *     variable bag and every `{{var}}` binding renders unresolved — while the same screen
 *     served over the API renders correctly. Parse it back to an object.
 *   - resolution fields: register custom widths and expose `baseScreenResolution`, matching
 *     parseNewFormat. Without the registration, breakpoints keyed by a custom resolution id
 *     resolve to no width and their @media rules never emit.
 */
export const normalizeScreenSettings = (settings: any) => {
  if (!settings || typeof settings !== 'object') return settings;
  const rawVariables = settings.variables;
  const parsedVariables = typeof rawVariables === 'string' ? parseIfJSON(rawVariables) : rawVariables;
  const customResolutions = settings?.custom_resolutions ?? settings?.customResolutions;
  registerCustomResolutions(customResolutions);
  return {
    ...settings,
    variables: (parsedVariables && typeof parsedVariables === 'object') ? parsedVariables : {},
    baseScreenResolution: settings?.base_screen_resolution ?? settings?.baseScreenResolution,
    customResolutions,
  };
};

export const parseOldFormat = (data: any) => {
  const { sections, settings, localization } = data || {};
  const [first] = sections || [];

  // Old format keeps sections/settings at the top level; localization is a
  // top-level sibling (same level as `config`) with a `settings` fallback.
  // Refine <html lang> the same way parseNewFormat does so the legacy
  // (sections-at-top) path isn't silently skipped. No-op when absent.
  applyDocumentLanguage(localization ?? settings?.localization);
  // Previously this hardcoded `settings: {}`, which discarded the screen's
  // resolution config (base_screen_resolution, custom_resolutions) on every
  // legacy (sections-at-top) response. That left pageSettings.baseScreenResolution
  // undefined, which collapses the per-breakpoint @media math in
  // generateComponentStyles (a breakpoint whose styles never emit → the element
  // renders class-less/invisible). Normalize the same way parseNewFormat does so
  // the base resolution and custom widths survive. No-op when settings is absent.
  const customResolutions = settings?.custom_resolutions ?? settings?.customResolutions;
  registerCustomResolutions(customResolutions);
  const enhancedSettings = settings
    ? {
      ...settings,
      baseScreenResolution: settings?.base_screen_resolution ?? settings?.baseScreenResolution,
      customResolutions,
    }
    : {};
  // FE-4733 — the template layer goes underneath the screen's own variables.
  const settingsWithTemplates = withTemplateVariables(enhancedSettings, [first], data?.templates);
  // The legacy shape predates templates, but a cached/legacy payload that does
  // carry the map must assemble the same way rather than rendering a bare
  // reference. Falls back to the original node when the reference is
  // unresolved, so this can never turn a legacy screen into nothing.
  const [assembledFirst] = applyStyleClasses(
    expandTemplateRefs([first], data?.templates),
    data?.classes,
  );
  return { page: migrateSchemeNode(assembledFirst ?? first), settings: settingsWithTemplates, variableData: {}, screenId: undefined, liveUpdates: false };
}

// Transform session_data from snake_case to camelCase
export function sessionDataTransformer(sessionData: any): any {
  if (!sessionData || typeof sessionData !== 'object') return sessionData;
  
  const transformed = { ...sessionData };
  
  if ('session_id' in transformed) {
    transformed.sessionId = transformed.session_id;
    delete transformed.session_id;
  }
  
  if ('device_id' in transformed) {
    transformed.deviceId = transformed.device_id;
    delete transformed.device_id;
  }
  
  return transformed;
}

export const parseNewFormat = (data: any) => {
  const { screen, variable_data: variableData, parameters, live_updates: liveUpdates, screen_id, localization } = data || {};
  const { sections: rawSections, settings, id } = screen || {};

  // Backend may attach `localization` at the response top level or nested on
  // the screen/settings — read defensively and refine <html lang>.
  const screenLocalization = localization ?? screen?.localization ?? settings?.localization;
  applyDocumentLanguage(screenLocalization);
  // FE-4451 — assemble template references BEFORE the protocol migration, so a
  // template BODY (which arrives straight from the map, never having passed
  // through a screen parse) gets the same legacy-field migration every other
  // node does. Expanding afterwards would leave template content on the old
  // protocol while the screen around it moved on.
  // FE-4517 — classes resolve AFTER template assembly, so a class named inside a
  // template body is resolved too: the body arrives straight from the templates
  // map and has never passed through a screen parse. Same ordering reason as the
  // migration below.
  const assembledSections = Array.isArray(rawSections)
    ? applyStyleClasses(expandTemplateRefs(rawSections, data?.templates), data?.classes)
    : rawSections;
  const sections = Array.isArray(assembledSections)
    ? assembledSections.map(migrateSchemeNode)
    : assembledSections;

  // Merge parameters into variableData at the top level
  const mergedVariableData = { ...variableData, ...parameters };
  
  // Unescape the script content if present (it's escaped during save as <\\/script>)
  const unescapedScript = settings?.script 
    ? settings.script.replace(/<\\\/script>/gi, '</script>')
    : settings?.script;
  
  // Normalize resolution fields: backend sends snake_case
  // (base_screen_resolution, custom_resolutions) but components read camelCase.
  // Register custom resolution widths so breakpoints keyed by custom ids resolve.
  const customResolutions = settings?.custom_resolutions ?? settings?.customResolutions;
  registerCustomResolutions(customResolutions);

  // Add live_updates to settings and unescape script.
  // FE-4733 — and lay the template layer underneath the screen's own variables:
  // every placement's declared locals, keyed by its path, so a qualified
  // `{{<placement>.action.state}}` has its default before any event writes it.
  const enhancedSettings = {
    ...withTemplateVariables(settings, rawSections, data?.templates),
    script: unescapedScript,
    live_updates: liveUpdates,
    localization: screenLocalization,
    baseScreenResolution: settings?.base_screen_resolution ?? settings?.baseScreenResolution,
    customResolutions,
  };

  if (mergedVariableData && Object.keys(mergedVariableData).length > 0) {
    const remainingVariableData = { ...mergedVariableData };
    Object.keys(mergedVariableData).forEach(key => {
      if (key === 'c' || key.startsWith('c.')) {
        setGlobalVariable(key, mergedVariableData[key]);
        delete remainingVariableData[key];
      }
    });
    return { page: sections, settings: enhancedSettings, variableData: remainingVariableData, screenId: id || screen_id };
  }

  return { page: sections, settings: enhancedSettings, variableData: mergedVariableData, screenId: id || screen_id };
}

// Helper function to recursively remove components by ID
export function recursiveRemoveById(items: ItemType[], idsToRemove: string[]): ItemType[] {
  return items
    .filter(item => !idsToRemove.includes(item.id))
    .map(item => ({
      ...item,
      items: item.items ? recursiveRemoveById(item.items, idsToRemove) : item.items
    }));
}

// Helper function to recursively replace/merge components by ID
export function recursiveReplaceById(items: ItemType[], replaceOperations: any[]): ItemType[] {
  // Separate operations into moves (with parent_id) and regular updates
  const moveOperations = replaceOperations.filter((op) => op.parent_id !== undefined);
  const updateOperations = replaceOperations.filter((op) => op.parent_id === undefined);

  // First pass: collect nodes to be moved (with their complete subtrees)
  const nodesToMove: Map<string, ItemType> = new Map();

  const collectNodesToMove = (itemList: ItemType[]) => {
    itemList.forEach((item) => {
      const moveOp = moveOperations.find((op) => op.id === item.id);
      if (moveOp) {
        // Store the complete node with all its children
        nodesToMove.set(item.id, item);
      }
      if (item.items && Array.isArray(item.items)) {
        collectNodesToMove(item.items);
      }
    });
  };
  collectNodesToMove(items);

  // Second pass: remove nodes that are being moved and apply regular updates
  const processItems = (itemList: ItemType[]): ItemType[] => itemList
    .filter((item) => !nodesToMove.has(item.id)) // Remove nodes that will be moved
    .map((item) => {
      // Find if there's a regular update operation for this item
      const updateOp = updateOperations.find((op) => op.id === item.id);

      if (updateOp) {
        // Extract the properties to merge - support both formats:
        // 1. { id, component: {...} } - wrapped in component property
        // 2. { id, value, ... } - direct properties
        const {
          id, component, position, ...directProps
        } = updateOp;
        const propsToMerge = component || directProps;

        // Deep merge the properties from update operation with existing item
        const mergedItem = merge({}, item, propsToMerge, { id: item.id });

        // If item has nested items, recursively process them
        if (mergedItem.items && Array.isArray(mergedItem.items)) {
          mergedItem.items = processItems(mergedItem.items);
        }

        return mergedItem;
      }

      // No update operation for this item, but check nested items
      if (item.items && Array.isArray(item.items)) {
        return {
          ...item,
          items: processItems(item.items),
        };
      }

      return item;
    });

  let processedItems = processItems(items);

  // Third pass: insert moved nodes at their new parent locations
  if (moveOperations.length > 0) {
    const insertMovedNodes = (itemList: ItemType[]): ItemType[] => itemList.map((item) => {
      // Find move operations that target this item as the new parent
      const movesToThisParent = moveOperations.filter((op) => op.parent_id === item.id);

      if (movesToThisParent.length > 0) {
        // Ensure items array exists
        const currentItems = item.items || [];

        // Sort moves by position to ensure correct order of insertion
        const sortedMoves = [...movesToThisParent].sort((a, b) => {
          const posA = a.position !== undefined ? parseInt(String(a.position), 10) : Number.MAX_SAFE_INTEGER;
          const posB = b.position !== undefined ? parseInt(String(b.position), 10) : Number.MAX_SAFE_INTEGER;
          return posA - posB;
        });

        // Insert each moved node with its complete subtree
        sortedMoves.forEach((moveOp) => {
          const nodeToMove = nodesToMove.get(moveOp.id);
          if (nodeToMove) {
            // Apply any property updates from the move operation (excluding parent_id)
            const {
              id, parent_id: parentId, component, position, ...propsToUpdate
            } = moveOp;
            const updatedNode = Object.keys(propsToUpdate).length > 0
              ? merge({}, nodeToMove, propsToUpdate)
              : nodeToMove;

            const targetPos = parseInt(String(position), 10);

            if (!Number.isNaN(targetPos) && targetPos >= 0 && targetPos <= currentItems.length) {
              currentItems.splice(targetPos, 0, updatedNode);
            } else {
              currentItems.push(updatedNode);
            }
          }
        });

        return {
          ...item,
          items: currentItems.length > 0 ? insertMovedNodes(currentItems) : currentItems,
        };
      }

      // No moves to this parent, but check nested items
      if (item.items && Array.isArray(item.items)) {
        return {
          ...item,
          items: insertMovedNodes(item.items),
        };
      }

      return item;
    });

    // Handle moves to root level (parent_id is empty string or root identifier)
    const movesToRoot = moveOperations.filter((op) => op.parent_id === '');
    if (movesToRoot.length > 0) {
      const sortedMoves = [...movesToRoot].sort((a, b) => {
        const posA = a.position !== undefined ? parseInt(String(a.position), 10) : Number.MAX_SAFE_INTEGER;
        const posB = b.position !== undefined ? parseInt(String(b.position), 10) : Number.MAX_SAFE_INTEGER;
        return posA - posB;
      });

      sortedMoves.forEach((moveOp) => {
        const nodeToMove = nodesToMove.get(moveOp.id);
        if (nodeToMove) {
          // Apply any property updates from the move operation (excluding parent_id)
          const {
            id, parent_id: parentId, component, position, ...propsToUpdate
          } = moveOp;
          const updatedNode = Object.keys(propsToUpdate).length > 0
            ? merge({}, nodeToMove, propsToUpdate)
            : nodeToMove;

          const targetPos = parseInt(String(position), 10);
          if (!Number.isNaN(targetPos) && targetPos >= 0 && targetPos <= processedItems.length) {
            processedItems.splice(targetPos, 0, updatedNode);
          } else {
            processedItems.push(updatedNode);
          }
        }
      });
    }

    processedItems = insertMovedNodes(processedItems);
  }

  // Fourth pass: reposition items if position property is specified
  const repositionOperations = replaceOperations.filter((op) => op.position !== undefined);
  if (repositionOperations.length > 0) {
    const repositionItems = (itemList: ItemType[]): ItemType[] => itemList.map((item) => {
      // Check if any children need repositioning
      if (item.items && Array.isArray(item.items) && item.items.length > 0) {
        const currentItems = [...item.items];
        let hasChanges = false;

        // Find operations that target children of this parent
        repositionOperations.forEach((op) => {
          const targetIndex = currentItems.findIndex((child) => child.id === op.id);
          if (targetIndex !== -1) {
            const targetPosition = parseInt(String(op.position), 10);

            // Only reposition if the target position is valid and different from current
            if (!Number.isNaN(targetPosition) && targetPosition >= 0 && targetPosition !== targetIndex) {
              const [movedItem] = currentItems.splice(targetIndex, 1);
              const insertPosition = Math.min(targetPosition, currentItems.length);
              currentItems.splice(insertPosition, 0, movedItem);
              hasChanges = true;
            }
          }
        });

        // Recursively process nested items
        const processedChildren = repositionItems(currentItems);

        return {
          ...item,
          items: processedChildren,
        };
      }

      return item;
    });

    // Handle root-level repositioning
    const rootReposOperations = repositionOperations.filter((op) =>
      // Check if the item exists at root level
      processedItems.findIndex((item: ItemType) => item.id === op.id) !== -1);

    if (rootReposOperations.length > 0) {
      rootReposOperations.forEach((op) => {
        const currentIndex = processedItems.findIndex((item: ItemType) => item.id === op.id);
        if (currentIndex !== -1) {
          const targetPosition = parseInt(String(op.position), 10);

          if (!Number.isNaN(targetPosition) && targetPosition >= 0 && targetPosition !== currentIndex) {
            const [movedItem] = processedItems.splice(currentIndex, 1);
            const insertPosition = Math.min(targetPosition, processedItems.length);
            processedItems.splice(insertPosition, 0, movedItem);
          }
        }
      });
    }

    processedItems = repositionItems(processedItems);
  }

  return processedItems;
}

// Helper function to recursively insert components at specified parent locations
// Supports both old format (components: array) and new format (component: object, position: number)
export function recursiveInsertById(items: ItemType[], insertOperations: any[]): ItemType[] {
  return items.map(item => {
    // Check if this item is a parent for any insert operation
    const insertOps = insertOperations.filter(op => op.parent_id === item.id);
    
    if (insertOps.length > 0) {
      // Process all insert operations for this parent
      const newItems = [...(item.items || [])];
      
      insertOps.forEach(insertOp => {
        // Old format: { parent_id, components: [...] }
        if (insertOp.components && Array.isArray(insertOp.components)) {
          // Append all components to the end (old behavior)
          newItems.push(...insertOp.components);
        }
        // New format: { parent_id, position, component: {...} }
        else if (insertOp.component) {
          const {component} = insertOp;
          const {position} = insertOp;
          
          // If position is specified and valid, insert at that position
          if (typeof position === 'number' && position >= 0) {
            // Clamp position to valid range
            const insertIndex = Math.min(position, newItems.length);
            newItems.splice(insertIndex, 0, component);
          } else {
            // If no position specified, append to end
            newItems.push(component);
          }
        }
      });
      
      return {
        ...item,
        items: newItems
      };
    }
    
    // Recursively process child items
    if (item.items && item.items.length > 0) {
      return {
        ...item,
        items: recursiveInsertById(item.items, insertOperations)
      };
    }
    
    return item;
  });
}

// Main data updater function for handling mode: "update"
export function updateDataWithModeUpdate(
  currentData: any,
  updateData: any,
  currentVariableData: any = {}
): { page: any; variableData: any; shouldUpdateCache: boolean } {
  const { mode, parameters, updates, update_cache } = updateData || {};
  
  // If not update mode, return original data
  if (mode !== 'update') {
    return {
      page: currentData,
      variableData: currentVariableData,
      shouldUpdateCache: false
    };
  }

  let updatedPage = clearSkeletonProperttyFromScheme(currentData);

  // FE-4577 — resolve templates and style classes on the nodes this patch brings
  // IN, before they are merged into the tree.
  //
  // A full screen response gets this from `parseNewFormat`, but an `updates`
  // frame never passed through it, so a patched-in node naming a template or a
  // `style.class` rendered as a bare unresolved reference. The protocol
  // explicitly delivers the classes an update needs, and by this point the trust
  // boundary has already merged the store in, so `updateData.classes` /
  // `.templates` hold the complete maps whether the bodies arrived in this frame
  // or in an earlier one.
  //
  // Both helpers are identity-preserving when there is nothing to resolve
  // (`containsTemplateRef` / the empty-class-map guard), so a patch that uses
  // neither is untouched and pays no rebuild.
  const resolveIncoming = (nodes: any): any =>
    applyStyleClasses(expandTemplateRefs(nodes, updateData?.templates), updateData?.classes);

  const resolvedReplace = Array.isArray(updates?.replace)
    ? resolveIncoming(updates.replace)
    : updates?.replace;
  const resolvedInsert = Array.isArray(updates?.insert)
    ? updates.insert.map((op: any) => {
      if (Array.isArray(op?.components)) {
        return { ...op, components: resolveIncoming(op.components) };
      }
      if (op?.component) {
        const [resolvedComponent] = resolveIncoming([op.component]);
        return { ...op, component: resolvedComponent };
      }
      return op;
    })
    : updates?.insert;

  // Apply remove operations
  if (updates?.remove && Array.isArray(updates.remove)) {
    updatedPage = recursiveRemoveById(updatedPage, updates.remove);
  }

  // Apply replace operations (merge styles with existing items)
  if (Array.isArray(resolvedReplace)) {
    updatedPage = recursiveReplaceById(updatedPage, resolvedReplace);
  }

  // Apply insert operations
  if (Array.isArray(resolvedInsert)) {
    // Separate root-level inserts from nested inserts
    const rootInserts = resolvedInsert.filter((op: any) => op.parent_id === null || op.parent_id === '');
    const nestedInserts = resolvedInsert.filter((op: any) => op.parent_id !== null && op.parent_id !== '');

    // First, recursively apply nested inserts
    if (nestedInserts.length > 0) {
      updatedPage = recursiveInsertById(updatedPage, nestedInserts);
    }

    // Then, handle root-level inserts
    if (rootInserts.length > 0) {
      // Process each root insert operation
      rootInserts.forEach((insertOp: any) => {
        // Old format: { parent_id, components: [...] }
        if (insertOp.components && Array.isArray(insertOp.components)) {
          updatedPage.push(...insertOp.components);
        }
        // New format: { parent_id, position, component: {...} }
        else if (insertOp.component) {
          const { component, position } = insertOp;
          const pos = parseInt(String(position), 10) || 0;

          // Insert component at specified position in root array
          if (pos >= 0 && pos <= updatedPage.length) {
            updatedPage.splice(pos, 0, component);
          } else {
            // If position is out of bounds, append to end
            updatedPage.push(component);
          }
        }
      });
    }
  }
  
  // FE-4733 — placements this patch brings in own template locals too. Their
  // defaults go UNDERNEATH the current bag, so a value an event already wrote
  // under the same placement path survives a repeated response (UC-6).
  const incomingNodes = [
    ...(Array.isArray(updates?.replace) ? updates.replace : []),
    ...(Array.isArray(updates?.insert)
      ? updates.insert.flatMap((op: any) => (
        Array.isArray(op?.components) ? op.components : (op?.component ? [op.component] : [])
      ))
      : []),
  ];
  const variablesWithTemplates = layerTemplateVariables(
    collectTemplateVariables(incomingNodes, updateData?.templates),
    currentVariableData,
  );

  // Merge parameters into variableData
  const updatedVariableData = parameters 
    ? merge({}, variablesWithTemplates, parameters)
    : variablesWithTemplates;
  
  return {
    page: updatedPage,
    variableData: updatedVariableData,
    shouldUpdateCache: !!update_cache
  };
}

/**
 * FE-4677 — fill `{{token}}` segments in an endpoint PATH from the action's
 * resolved parameters.
 *
 * Only the part before the first `?`/`#` is touched, so query and fragment
 * templates keep flowing through `replacePlaceholdersWithVariables` untouched.
 *
 * Per token, in order:
 *   1. `params[key]` — the action's own parameter row wins. This is what makes
 *      `loan_ref = {{this.loan_ref}}` (table row), `{{loan.loan_ref}}` (page
 *      variable) and a static `daily` all work: they are already resolved to a
 *      concrete value by the time we get here.
 *   2. `getNestedValue2(variables, key)` — page-variable fallback, so screens
 *      saved before this ticket keep working and dotted `{{a.b}}` resolves.
 *   3. otherwise the token is reported in `missing`.
 *
 * `undefined`, `null`, `''` and whitespace-only all count as MISSING, never as an
 * empty substitution: silently shipping `/admin/loan/` can hit a different
 * endpoint entirely (see FE-4670 — `/x` and `/x/{{id}}` are distinct routes).
 *
 * Values are URI-encoded, so a path parameter is always exactly one segment and
 * can never inject a `/`, a query string or a URL scheme.
 *
 * @returns `path` with tokens substituted, `used` = keys taken from `params`
 *          (the caller strips these from the body/query), `missing` = unresolved.
 */
export const substitutePathParams = (
  template: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params: Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables: any,
): { path: string; used: string[]; missing: string[] } => {
  if (typeof template !== 'string' || !template) {
    return { path: template, used: [], missing: [] };
  }

  // Keep the query/fragment aside — a token there is not a path parameter.
  const splitAt = ((): number => {
    const q = template.indexOf('?');
    const h = template.indexOf('#');
    if (q === -1) return h;
    if (h === -1) return q;
    return Math.min(q, h);
  })();
  const pathPart = splitAt === -1 ? template : template.slice(0, splitAt);
  const rest = splitAt === -1 ? '' : template.slice(splitAt);

  const used = new Set<string>();
  const missing = new Set<string>();

  const isBlank = (v: unknown): boolean => v === undefined || v === null
    || (typeof v === 'string' && v.trim() === '');

  const resolvedPath = pathPart.replace(/\{\{([^{}]+)\}\}/g, (whole, rawKey) => {
    const key = String(rawKey).trim();
    if (!key) return whole;

    // `{{this.*}}` (repeatable row scope) and `{{_l.*}}` (localisation) are owned by
    // other passes — replacePlaceholdersWithVariables skips them for exactly this
    // reason. Leave them untouched and do NOT report them as missing, so this helper
    // stays strictly additive: a token those passes left behind reaches the URL
    // literally, just as it did before FE-4677.
    if (key === 'this' || key.startsWith('this.') || key.startsWith('_l.')) return whole;

    const fromParams = params ? params[key] : undefined;
    if (!isBlank(fromParams)) {
      used.add(key);
      return encodeURIComponent(typeof fromParams === 'object' ? JSON.stringify(fromParams) : String(fromParams));
    }

    const fromVars = getNestedValue2(variables, key);
    if (!isBlank(fromVars)) {
      return encodeURIComponent(typeof fromVars === 'object' ? JSON.stringify(fromVars) : String(fromVars));
    }

    missing.add(key);
    return whole;
  });

  return { path: resolvedPath + rest, used: [...used], missing: [...missing] };
};
